import { defineCollection } from "@rebasepro/cms-types";
import type { CollectionConfig } from "@rebasepro/types";
import gamePlayersCollection from "./game-players";
import dailyScoresCollection from "./daily-scores";
import challengeScoresCollection from "./challenge-scores";
import reportsCollection from "./reports";
import soloSavesCollection from "./solo-saves";

// Everyone SongBuzz knows, one row per person: an account ("u:<id>": Google, Apple, email, or the guest every phone
// signs in as) or, for Solo played without one, the device. backend/src/games.ts rebuilds it every five minutes from
// what they did (party game results, the Daily Gig, challenges, the Solo save, Friends), so it's read-only here: the
// tables it's built from are where things change.
const SIGN_IN: Record<string, string> = { google: "Google", apple: "Apple", email: "Email", guest: "Guest", device: "Solo device", deleted: "Deleted account" };
const many = (target: () => CollectionConfig, relationName: string, table: string, to: string) => ({
    type: "relation" as const,
    admin: { readOnly: true },
    relation: { kind: "via" as const, cardinality: "many" as const, target, relationName, joinPath: [{ table, on: { from: "id", to } }] },
});

const playersCollection = defineCollection({
    name: "Players",
    singularName: "Player",
    slug: "players",
    table: "songbuzz_players",
    description: "Everyone who has played: party games, Daily Gigs, challenges, Solo, friends. Open one to see all of it.",
    securityRules: [{ operation: "select", roles: ["admin"] }],
    properties: {
        id: { name: "Player id", type: "string", isId: "manual", admin: { readOnly: true } },
        name: { name: "Name", type: "string", admin: { readOnly: true } },
        signIn: { name: "Signs in with", type: "string", admin: { readOnly: true, Preview: "../../admin/src/previews/Words", customProps: { say: "signIn" } } },
        email: { name: "Email", type: "string", admin: { readOnly: true } },
        firstSeen: { name: "First seen", type: "date", admin: { readOnly: true } },
        lastSeen: { name: "Last seen", type: "date", admin: { readOnly: true } },
        partyGames: { name: "Party games", type: "number", columnType: "integer", defaultValue: 0, validation: { required: true }, admin: { readOnly: true } },
        partyWins: { name: "Party games won", type: "number", columnType: "integer", defaultValue: 0, validation: { required: true }, admin: { readOnly: true } },
        dailyGigs: { name: "Daily Gigs", type: "number", columnType: "integer", defaultValue: 0, validation: { required: true }, admin: { readOnly: true } },
        bestDaily: { name: "Best Daily Gig", type: "number", columnType: "bigint", admin: { readOnly: true } },
        dailyStreak: { name: "Daily Gig streak", type: "number", columnType: "integer", admin: { readOnly: true } },
        challenges: { name: "Challenges", type: "number", columnType: "integer", defaultValue: 0, validation: { required: true }, admin: { readOnly: true } },
        soloRuns: { name: "Solo runs", type: "number", columnType: "integer", admin: { readOnly: true } },
        soloWins: { name: "Solo runs won", type: "number", columnType: "integer", admin: { readOnly: true } },
        soloFans: { name: "Solo fans", type: "number", columnType: "integer", admin: { readOnly: true } },
        soloBest: { name: "Best Solo tour score", type: "number", columnType: "bigint", admin: { readOnly: true } },
        songsNamed: { name: "Songs named in Solo", type: "number", columnType: "integer", admin: { readOnly: true } },
        friends: { name: "Friends", type: "number", columnType: "integer", defaultValue: 0, validation: { required: true }, admin: { readOnly: true } },
        reported: { name: "Times reported", type: "number", columnType: "integer", defaultValue: 0, validation: { required: true }, admin: { readOnly: true } },
        updatedAt: { name: "Updated", type: "date", admin: { readOnly: true } },
        games: { name: "Party game results", ...many((): CollectionConfig => gamePlayersCollection, "games", "songbuzz_game_players", "player") },
        dailyScores: { name: "Daily Gig scores", ...many((): CollectionConfig => dailyScoresCollection, "dailyScores", "songbuzz_solo_daily", "sid") },
        challengeScores: { name: "Challenge scores", ...many((): CollectionConfig => challengeScoresCollection, "challengeScores", "songbuzz_solo_duel_score", "sid") },
        soloSave: { name: "Solo progress", ...many((): CollectionConfig => soloSavesCollection, "soloSave", "songbuzz_solo_save", "who") },
        reports: { name: "Reports about them", ...many((): CollectionConfig => reportsCollection, "reports", "songbuzz_friend_report", "about") },
    },
    admin: {
        icon: "User",
        group: "Players",
        openEntityMode: "side_panel",
        sort: ["lastSeen", "desc"],
        display: {
            // no name given anywhere: say what they are, with the start of their id to tell them apart
            title: ({ entity }) => entity.values.name || `${entity.values.signIn === "device" ? "Solo player" : SIGN_IN[String(entity.values.signIn)] || "Player"} · ${String(entity.id).replace(/^u:/, "").slice(0, 6)}`,
            subtitle: ({ entity }) => [SIGN_IN[String(entity.values.signIn)] || entity.values.signIn, entity.values.email].filter(Boolean).join(" · ") || undefined,
            status: ({ entity }) => (entity.values.reported ?? 0) > 0 ? `Reported ${entity.values.reported}×` : undefined,
            date: "lastSeen",
        },
        listProperties: ["partyGames", "dailyGigs", "challenges", "soloRuns", "lastSeen"],
        form: {
            sections: [
                { key: "who", title: "Who", properties: ["name", "signIn", "email", "firstSeen", "lastSeen"] },
                { key: "party", title: "Party games", properties: ["partyGames", "partyWins"] },
                { key: "solo", title: "Solo", properties: ["soloRuns", "soloWins", "soloFans", "soloBest", "songsNamed", "dailyGigs", "bestDaily", "dailyStreak", "challenges"] },
                { key: "friends", title: "Friends", properties: ["friends", "reported"] },
                { key: "results", title: "What they played", properties: ["soloSave", "games", "dailyScores", "challengeScores", "reports"] },
                { key: "ids", title: "Behind the scenes", collapsed: true, properties: ["id", "updatedAt"] },
            ],
        },
    },
});

export default playersCollection;
